import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Grid, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import type { Contact, Customer, Opportunity, Partner, PartnerRelationshipType } from '../../api/types';
import { PARTNER_RELATIONSHIP_TYPES } from '../../api/types';
import { useCustomer, useMeta, usePartners, useSaveOpportunity } from '../../api/hooks';
import { ApiError } from '../../api/client';
import { blankToNull } from '../common/format';
import { useErrorMessage } from '../common/Common';
import { ContactFormDialog } from './ContactFormDialog';

export function OpportunityFormDialog({
  open, customerId, contacts, opportunity, onClose, onSaved,
}: {
  open: boolean;
  customerId: number;
  contacts: Contact[];
  opportunity?: Opportunity | null;
  onClose: () => void;
  onSaved?: (opportunity: Opportunity) => void;
}) {
  const { t } = useTranslation();
  const describeError = useErrorMessage();
  const save = useSaveOpportunity();
  const { data: meta } = useMeta();
  const { data: partners } = usePartners();
  const { data: customer } = useCustomer(open ? customerId : 0);

  const [title, setTitle] = useState('');
  const [pain, setPain] = useState('');
  const [vision, setVision] = useState('');
  const [value, setValue] = useState('');
  const [estimatedValue, setEstimatedValue] = useState('');
  const [currency, setCurrency] = useState('NZD');
  const [expectedCloseDate, setExpectedCloseDate] = useState('');
  const [championContactId, setChampionContactId] = useState('');
  const [economicBuyerContactId, setEconomicBuyerContactId] = useState('');
  const [source, setSource] = useState('manual');
  const [nextStep, setNextStep] = useState('');
  const [nextStepDueDate, setNextStepDueDate] = useState('');
  const [partner, setPartner] = useState<Partner | null>(null);
  const [relationshipType, setRelationshipType] = useState<PartnerRelationshipType | ''>('');
  const [margin, setMargin] = useState('');
  const [touched, setTouched] = useState(false);

  // A contact created from inside this dialog is held locally so it is selectable straight away,
  // rather than only after the customer's contact query refetches.
  const [contactFor, setContactFor] = useState<'champion' | 'buyer' | null>(null);
  const [addedContacts, setAddedContacts] = useState<Contact[]>([]);
  const selectableContacts = [
    ...contacts,
    ...addedContacts.filter((a) => !contacts.some((c) => c.id === a.id)),
  ];
  const partnerResolvedFor = useRef<number | null>(null);

  const partnerOptions = partners?.items ?? [];

  useEffect(() => {
    if (!open) return;
    setTitle(opportunity?.title ?? '');
    setPain(opportunity?.pain ?? '');
    setVision(opportunity?.vision ?? '');
    setValue(opportunity?.value ?? '');
    setEstimatedValue(opportunity?.estimatedValue != null ? String(opportunity.estimatedValue) : '');
    setCurrency(opportunity?.currency ?? meta?.defaultCurrency ?? 'NZD');
    setExpectedCloseDate(opportunity?.expectedCloseDate ?? '');
    setChampionContactId(opportunity?.championContactId ? String(opportunity.championContactId) : '');
    setEconomicBuyerContactId(opportunity?.economicBuyerContactId ? String(opportunity.economicBuyerContactId) : '');
    setSource(opportunity?.source ?? 'manual');
    setNextStep(opportunity?.nextStep ?? '');
    setNextStepDueDate(opportunity?.nextStepDueDate ?? '');
    setPartner(null);
    partnerResolvedFor.current = null;
    setRelationshipType(opportunity?.partnerRelationshipType ?? '');
    setMargin(opportunity?.partnerMarginPercent != null ? String(opportunity.partnerMarginPercent) : '');
    setTouched(false);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, opportunity, meta?.defaultCurrency]);

  // The partner list can arrive after the dialog is already open. Resolving it here rather than in
  // the reset effect above means a late-arriving query can never wipe what the user has typed.
  useEffect(() => {
    if (!open || !partners) return;
    const key = opportunity?.id ?? 0;
    if (partnerResolvedFor.current === key) return;
    partnerResolvedFor.current = key;
    setPartner(partners.items.find((p) => p.id === opportunity?.partnerId) ?? null);
  }, [open, opportunity, partners]);

  const titleMissing = title.trim().length === 0;
  const marginValue = margin.trim() === '' ? null : Number(margin);
  const marginInvalid = marginValue !== null
    && (Number.isNaN(marginValue) || marginValue < 0 || marginValue > 100);
  const serverErrors = save.error instanceof ApiError ? save.error.fieldErrors : {};

  const customerPartner = (customer as Customer | undefined)?.partnerId ?? null;
  const canResetToCustomerDefault = customerPartner !== null
    && (partner?.id ?? null) !== null
    && !(opportunity?.partnerIsInherited && partner?.id === customerPartner);

  /** Selecting a partner seeds the sub-fields from that partner's defaults, so the common case is
   *  "pick the partner and save". */
  const choosePartner = (next: Partner | null) => {
    setPartner(next);
    if (!next) {
      setRelationshipType('');
      setMargin('');
      return;
    }
    setRelationshipType(next.defaultRelationshipType);
    setMargin(next.defaultMarginPercent != null ? String(next.defaultMarginPercent) : '');
  };

  /** Sends the customer's partner with empty sub-fields — the exact shape the API treats as a
   *  fresh inherit, which restores the "follows the customer" behaviour. */
  const resetToCustomerDefault = () => {
    const inherited = partnerOptions.find((p) => p.id === customerPartner) ?? null;
    setPartner(inherited);
    setRelationshipType('');
    setMargin('');
  };

  const submit = async () => {
    setTouched(true);
    if (titleMissing || marginInvalid) return;
    const saved = await save.mutateAsync({
      id: opportunity?.id,
      body: {
        customerId,
        title: title.trim(),
        pain: blankToNull(pain),
        vision: blankToNull(vision),
        value: blankToNull(value),
        estimatedValue: estimatedValue.trim() === '' ? null : Number(estimatedValue),
        currency,
        expectedCloseDate: blankToNull(expectedCloseDate),
        probability: opportunity?.probability ?? null,
        championContactId: championContactId ? Number(championContactId) : null,
        economicBuyerContactId: economicBuyerContactId ? Number(economicBuyerContactId) : null,
        source: blankToNull(source),
        ownerUserId: opportunity?.ownerUserId ?? null,
        nextStep: blankToNull(nextStep),
        nextStepDueDate: blankToNull(nextStepDueDate),
        partnerId: partner?.id ?? null,
        partnerRelationshipType: partner ? (relationshipType || null) : null,
        partnerMarginPercent: partner ? marginValue : null,
      },
    });
    onSaved?.(saved);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {opportunity ? t('forms.opportunity.editTitle') : t('forms.opportunity.newTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {save.error && !Object.keys(serverErrors).length && (
            <Alert severity="error">{describeError(save.error)}</Alert>
          )}

          <TextField
            label={t('forms.opportunity.title')}
            required
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => setTouched(true)}
            error={(touched && titleMissing) || Boolean(serverErrors.title)}
            helperText={touched && titleMissing ? t('forms.opportunity.titleRequired') : serverErrors.title}
            inputProps={{ dir: 'auto', maxLength: 200, 'data-testid': 'opp-title' }}
          />

          <Divider textAlign="left">
            <Typography variant="overline" color="text.secondary">{t('opportunity.solutionSelling')}</Typography>
          </Divider>

          <TextField
            label={t('opportunity.pain')}
            multiline
            minRows={2}
            value={pain}
            onChange={(e) => setPain(e.target.value)}
            inputProps={{ dir: 'auto', 'data-testid': 'opp-pain' }}
          />
          <TextField
            label={t('opportunity.vision')}
            multiline
            minRows={2}
            value={vision}
            onChange={(e) => setVision(e.target.value)}
            inputProps={{ dir: 'auto', 'data-testid': 'opp-vision' }}
          />
          <TextField
            label={t('opportunity.value')}
            multiline
            minRows={2}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            inputProps={{ dir: 'auto', 'data-testid': 'opp-value' }}
          />

          <Divider textAlign="left">
            <Typography variant="overline" color="text.secondary">{t('opportunity.money')}</Typography>
          </Divider>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                fullWidth
                label={t('opportunity.estimatedValue')}
                type="number"
                value={estimatedValue}
                onChange={(e) => setEstimatedValue(e.target.value)}
                error={Boolean(serverErrors.estimatedValue)}
                helperText={serverErrors.estimatedValue}
                inputProps={{ min: 0, step: '0.01', 'data-testid': 'opp-value-amount' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                select
                label={t('forms.opportunity.currency')}
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                data-testid="opp-currency"
              >
                {(meta?.currencies ?? ['NZD']).map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label={t('opportunity.expectedClose')}
                type="date"
                value={expectedCloseDate}
                onChange={(e) => setExpectedCloseDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'opp-close-date' }}
              />
            </Grid>
          </Grid>

          <Divider textAlign="left">
            <Typography variant="overline" color="text.secondary">{t('opportunity.people')}</Typography>
          </Divider>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label={t('opportunity.champion')}
                value={championContactId}
                onChange={(e) => setChampionContactId(e.target.value)}
                data-testid="opp-champion"
              >
                <MenuItem value="">{t('common.none')}</MenuItem>
                {selectableContacts.map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label={t('opportunity.economicBuyer')}
                value={economicBuyerContactId}
                onChange={(e) => setEconomicBuyerContactId(e.target.value)}
                data-testid="opp-buyer"
              >
                <MenuItem value="">{t('common.none')}</MenuItem>
                {selectableContacts.map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              {/* Without this the user has to abandon the opportunity to go and add the person
                  they are describing, then start again. */}
              <Button
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => setContactFor('champion')}
                data-testid="opp-add-champion"
              >
                {t('opportunity.addChampion')}
              </Button>
              <Button
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => setContactFor('buyer')}
                data-testid="opp-add-buyer"
              >
                {t('opportunity.addBuyer')}
              </Button>
            </Grid>
          </Grid>

          <Divider textAlign="left">
            <Typography variant="overline" color="text.secondary">{t('partner.partner')}</Typography>
          </Divider>

          <Grid container spacing={2} data-testid="opp-partner-group">
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={partnerOptions}
                value={partner}
                onChange={(_, next) => choosePartner(next)}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('partner.partner')}
                    error={Boolean(serverErrors.partnerId)}
                    helperText={serverErrors.partnerId}
                    inputProps={{ ...params.inputProps, 'data-testid': 'opp-partner-input' }}
                  />
                )}
                data-testid="opp-partner"
              />
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                {opportunity?.partnerIsInherited && (
                  <Typography variant="caption" color="text.secondary" data-testid="partner-inherited">
                    {t('partner.inheritedFromCustomer')}
                  </Typography>
                )}
                <Box sx={{ flexGrow: 1 }} />
                {canResetToCustomerDefault && (
                  <Button size="small" onClick={resetToCustomerDefault} data-testid="partner-reset">
                    {t('partner.resetToCustomerDefault')}
                  </Button>
                )}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label={t('partner.relationshipType')}
                value={relationshipType}
                disabled={!partner}
                onChange={(e) => setRelationshipType(e.target.value as PartnerRelationshipType | '')}
                data-testid="opp-partner-type"
              >
                <MenuItem value="">{t('common.none')}</MenuItem>
                {PARTNER_RELATIONSHIP_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{t(`partner.relationship.${type}`)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('partner.margin')}
                type="number"
                value={margin}
                disabled={!partner}
                onChange={(e) => setMargin(e.target.value)}
                error={(touched && marginInvalid) || Boolean(serverErrors.partnerMarginPercent)}
                helperText={
                  touched && marginInvalid
                    ? t('partner.marginRange')
                    : serverErrors.partnerMarginPercent ?? t('partner.marginHint')
                }
                inputProps={{ min: 0, max: 100, step: '0.01', 'data-testid': 'opp-partner-margin' }}
                InputProps={{ endAdornment: t('partner.marginUnit') }}
              />
            </Grid>
          </Grid>

          <Divider textAlign="left">
            <Typography variant="overline" color="text.secondary">{t('opportunity.nextStep')}</Typography>
          </Divider>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                label={t('opportunity.nextStep')}
                value={nextStep}
                onChange={(e) => setNextStep(e.target.value)}
                inputProps={{ dir: 'auto', maxLength: 500, 'data-testid': 'opp-next-step' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label={t('opportunity.due')}
                type="date"
                value={nextStepDueDate}
                onChange={(e) => setNextStepDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'opp-next-step-due' }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('forms.opportunity.source')}
                value={source}
                onChange={(e) => setSource(e.target.value)}
                helperText={t('forms.opportunity.sourceHint')}
                inputProps={{ dir: 'auto', maxLength: 100, 'data-testid': 'opp-source' }}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={save.isPending}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={save.isPending} data-testid="save-opportunity">
          {t('common.save')}
        </Button>
      </DialogActions>

      <ContactFormDialog
        open={contactFor !== null}
        customerId={customerId}
        onClose={() => setContactFor(null)}
        onSaved={(created) => {
          setAddedContacts((prev) => [...prev, created]);
          if (contactFor === 'champion') setChampionContactId(String(created.id));
          if (contactFor === 'buyer') setEconomicBuyerContactId(String(created.id));
        }}
      />
    </Dialog>
  );
}
